// Shared domain types — used by both client and server.
// Keep this file thin; full validation lives in zod schemas next to each route/feature.

export type UUID = string;
export type ISODate = string; // yyyy-mm-dd
export type ISOTimestamp = string; // RFC 3339

/* ---------------------------------------------------------------------------
 * Roles + permissions
 * -------------------------------------------------------------------------*/
export const ROLES = [
  'super_admin',
  'principal_adviser',
  'adviser',
  'support_staff',
  'compliance_officer',
  // 'client_portal' — reserved for future scope
] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  'client.view',
  'client.edit',
  'client.delete',
  'financial.view',
  'financial.edit',
  'advice.create',
  'advice.approve',
  'audit.read',
  'users.manage',
  'export.run',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/* ---------------------------------------------------------------------------
 * Client / account / holding shapes
 * -------------------------------------------------------------------------*/
export const SEGMENTS = ['Diamond', 'Platinum', 'Gold', 'Silver', 'Bronze', 'Inactive'] as const;
export type Segment = (typeof SEGMENTS)[number];
export type RiskProfile =
  | 'Conservative'
  | 'Moderately Conservative'
  | 'Balanced'
  | 'Growth'
  | 'High Growth';

export type AccountType = 'personal' | 'super' | 'pension' | 'trust' | 'smsf' | 'company';
export type TaxStatus = 'taxable' | 'concessional' | 'non_concessional' | 'tax_free' | 'mixed';

export type ContractType = 'buy' | 'sell';
export type AdviceDocStatus = 'draft' | 'review' | 'approved' | 'sent' | 'signed' | 'declined';
export type AdviceDocKind = 'soa' | 'roa';
export type FileNoteType =
  | 'call'
  | 'email'
  | 'text'
  | 'meeting'
  | 'advice_sent'
  | 'approval_received'
  | 'other';

/* ---------------------------------------------------------------------------
 * Compliance rule definitions
 * Rules are tested by ComplianceService on the server and surfaced to the UI.
 * -------------------------------------------------------------------------*/
export type ComplianceState = 'pass' | 'outstanding' | 'unknown';

export interface ComplianceRow {
  riskProfile: ComplianceState;
  factFind: ComplianceState;
  snapshot: ComplianceState;
  fileNote: ComplianceState;
  bestInterest: ComplianceState;
  soaSigned: ComplianceState;
  feeConsent: ComplianceState;
  updateNeeded: ComplianceState;
  writeSoa: ComplianceState;
  writeRoa: ComplianceState;
}

/* ---------------------------------------------------------------------------
 * Dashboard row
 * -------------------------------------------------------------------------*/
export interface DashboardRow {
  clientId: UUID;
  name: string;
  segment: Segment | null;
  callDue: ISODate | null;
  tnw: number;            // total net worth (AUD)
  cash: number;           // cash balance (AUD)
  pendingAdvice: boolean;
  proposedTrades: number;
  compliance: ComplianceRow;
  lastFileNote: ISODate | null;
  lastAdvice: ISODate | null;
  reviewDue: ISODate | null;
  emails: string[];
  christmasGift: boolean;
  eventAttendance: boolean;
  /** "SURNAME, First" (the Excel sheet's surname-first sort key). */
  sortName?: string;
  riskProfile?: string | null;
  lastSoa?: ISODate | null;
  lastRoa?: ISODate | null;
  feeConsentDate?: ISODate | null;
  sellsCount?: number;
  /** Used only to derive the next-birthday column. */
  dateOfBirth?: ISODate | null;
  /** Fee-consent renewal (shared/cadence.ts consentInfo). */
  consentStatus?: 'none' | 'current' | 'dueSoon' | 'renewalOpen' | 'lapsed';
  consentDue?: ISODate | null;
  consentDeadline?: ISODate | null;
  /** The dates behind the date-based compliance checks (for in-grid editing). */
  complianceDates?: Partial<Record<'riskProfile' | 'factFind' | 'snapshot' | 'bestInterest' | 'soaSigned' | 'feeConsent', ISODate | null>>;
  /** Open review case progress, e.g. "3/6 Actions agreed" (shared/reviewCase.ts); null when none open. */
  reviewStage?: string | null;
  /** Per watched security (GET /api/dashboard?watch=NUF,BSL): holding value, unrealised P/L, last trade date. */
  watch?: Record<string, { units: number; value: number; gain: number; lastTrade: ISODate | null }>;
}

export interface DashboardSummary {
  clientCount: number;
  totalTnw: number;
  totalCash: number;
  outstandingCount: Record<keyof ComplianceRow, number>;
  /**
   * Admin-only "needs attention" counts surfaced as a dashboard banner. Always
   * present in the response (zeros when the caller doesn't have `users.manage`),
   * so the client can render unconditionally without a feature-flag dance.
   */
  attentionCounts: {
    unlinkedFileNotes: number;
    openMatchCandidates: number;
  };
  /** Freshness of the prices behind TNW: held securities only (see shared/pricing.ts summarisePrices). */
  prices?: { oldest: string | null; newest: string | null; held: number; stale: number; manualStale?: number; unpriced?: number; unpricedCodes?: string[] };
  /** Currencies held with no FX-to-AUD rate available (valued unconverted, so flagged). */
  fxMissing?: string[];
}

/* ---------------------------------------------------------------------------
 * API envelope
 * -------------------------------------------------------------------------*/
export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}
